import { Button } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import { useFormikContext } from 'formik';
import { FormPart } from '@modules/shared/components/FormPart';
import { getOptions } from '@shared/commonFunctions';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { ASSET_TYPE_OPTIONS, assetConditions } from '@shared/constants/lists';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { RadioButtonListField } from '@shared/fields/RadioButtonListField';
import { TextInputField } from '@shared/fields/TextInputField';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { IOptionResponse } from '@shared/interfaces';
import { Roles } from '@shared/constants/constants';

import { IFormAsset, IVehicleTrim } from '../interfaces';
import { requestPullVehicleData } from '../requests';
import { PULL_STATUS } from '..';
import { TrimsModal } from './TrimsModal';
import { LicensePlateModal } from './LicensePlateModal';

interface IAssetFormProps {
  handleSubmit: (data: IFormAsset) => void;
  pickedDealId?: string;
}

export const AssetForm = ({ handleSubmit }: IAssetFormProps) => {
  const { isAdmin, userHasRoles } = useUserAccount();
  const { setValues, setFieldValue, values, setStatus } = useFormikContext<IFormAsset>();


  const hasEditLicensePlateRole = isAdmin || userHasRoles([Roles.EDIT_PLATE_2_VIN]);
  const [makeId, setMakeId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isLicensePlateModalOpen, setIsLicensePlateModalOpen] = useState(false);
  const { data: years = [], isValidating: isYearsLoading } = useSWR<IOptionResponse[]>(
    values?.assetType ? `${SERVICES.ASSET}/v2/kbb/years/${values?.assetType}` : null);
  const { data: makes = [], isValidating: isMakesLoading } = useSWR<IOptionResponse[]>(
    values?.year && values?.assetType ? `${SERVICES.ASSET}/v2/kbb/makes/${values?.year}/${values?.assetType}` : null
  );
  const { data: models = [], isValidating: isModelsLoading } = useSWR<IOptionResponse[]>(
    values?.year && makeId && values?.assetType ? `${SERVICES.ASSET}/v2/kbb/models/${makeId}/${values?.year}/${values?.assetType}` : null
  );
  const [isTrimsModalOpen, setTrimsModalOpen] = useState(0);
  const [kbbTrims, setKbbTrims] = useState<IVehicleTrim[]>([]);
  const [nadaTrims, setNadaTrims] = useState<IVehicleTrim[]>([]);

  const enablePlate2Vin = useMemo(() => (
    values.isPlateToVinSuccess ? hasEditLicensePlateRole : true
  ), [values.isPlateToVinSuccess, hasEditLicensePlateRole]);

  useEffect(() => {
    //This code is used to sort years option in descending orders.
    if (years !== undefined) {
      years.sort((yearOne: IOptionResponse, yearSecond: IOptionResponse) => yearSecond.id - yearOne.id);
    }
  }, [years]);

  useEffect(() => {
    const makeObj: IOptionResponse = makes?.find((a) => a.name === values.make) || ({} as IOptionResponse);
    setMakeId(makeObj.id);
  }, [values.make, makes]);

  const pullData = useCallback(async (values: IFormAsset) => {
    const vin = values?.vin;
    
    if (vin) {
      setStatus(PULL_STATUS);
      setLoading(true);

      const data = await requestPullVehicleData(vin);

      setLoading(false);

      if (data) {
        const { year, make, model, kbbTrim, nadaTrim } = data;

        if (
          (data.availableKbbTrims?.length === 1 && data.availableNadaTrims?.length === 0) ||
          (data.availableKbbTrims?.length === 0 && data.availableNadaTrims?.length === 1) ||
          (data.availableKbbTrims?.length === 1 && data.availableNadaTrims?.length === 1)
        ) {
          const updatedValues = { ...values, kbbTrim, nadaTrim, year, make, model };

          setValues(updatedValues);

          handleSubmit(updatedValues);
        } else if (data.availableKbbTrims && data.availableNadaTrims) {
          setValues({
            ...values,
            year,
            make,
            model,
          });

          setKbbTrims(data.availableKbbTrims);
          setNadaTrims(data.availableNadaTrims);

          setTrimsModalOpen(prevState => prevState + 1);
        } else {
          Notistack.generalError('Trim data is not available!');
        }
      }
    } else {
      Notistack.generalError('Vin is are required!');
    }
  }, []);

  const handleCloseTrim = useCallback(() => {
    setTrimsModalOpen(0);
  }, []);

  const handleSaveTrim = useCallback(() => {
    handleCloseTrim();
    handleSubmit(values);
  }, [values]);

  const formConfig = [
    {
      width: '100%',
      name: 'assetType',
      label: 'Type',
      options: ASSET_TYPE_OPTIONS,
      component: SelectAutocompleteField,
    },
    {
      width: '100%',
      name: 'mileage',
      label: 'Odometer',
      decimalScale: null,
      component: NumberInputField,
    },
    {
      width: '85%',
      name: 'vin',
      label: 'VIN',
      disabled: !enablePlate2Vin,
      component: TextInputField,
      enableButton: enablePlate2Vin,
      buttonText: 'Enter License Plate',
      inputProps: { maxLength: 17 },
      onBtnClick: () => setIsLicensePlateModalOpen(true),
      onChange: (ev: React.ChangeEvent<HTMLInputElement>) => setFieldValue('vin', ev.target.value.toUpperCase()),
    },
    {
      width: '5%',
      component: () => (
        <Button
          color="info"
          variant="text"
          size="small"
          style={{ marginLeft: '-20px', marginTop: 7 }}
          onMouseDown={() => pullData(values)}
        >
          Pull
        </Button>
      ),
    },
    {
      width: '100%',
      name: 'year',
      label: 'Year',
      options: getOptions(years),
      isLoading: isYearsLoading,
      isCreatable: true,
      component: SelectAutocompleteField,
      shouldUpdate: () => true,
    },
    {
      width: '100%',
      name: 'make',
      label: 'Make',
      options: getOptions(makes, 'name', 'name'),
      isLoading: isMakesLoading,
      isCreatable: true,
      component: SelectAutocompleteField,
      shouldUpdate: () => true,
    },
    {
      width: '100%',
      name: 'model',
      label: 'Model',
      options: getOptions(models, 'name', 'name'),
      isLoading: isModelsLoading,
      isCreatable: true,
      component: SelectAutocompleteField,
      shouldUpdate: () => true,
    },
    {
      width: '100%',
      name: 'kbbTrim',
      label: 'Kbb Trim',
      component: TextInputField,
    },
    {
      width: '100%',
      name: 'nadaTrim',
      label: 'JD Power Trim',
      component: TextInputField,
    },
    {
      width: '100%',
      name: 'assetCondition',
      options: assetConditions,
      component: RadioButtonListField,
    },
    {
      width: '100%',
      name: 'assetLienHolder.loan.term',
      type: 'number',
      component: TextInputField,
    },
    {
      width: '100%',
      name: 'assetLienHolder.remainingTerm',
      label: 'Remaining Term',
      component: NumberInputField,
    },
    {
      width: '100%',
      name: 'gapRefund',
      label: 'Gap Refund',
      prefix: '$',
      component: NumberInputField,
    },
    {
      width: '100%',
      name: 'vscRefund',
      label: 'VSC Refund',
      prefix: '$',
      component: NumberInputField,
    },
    {
      width: '100%',
      name: 'color',
      label: 'Color',
      component: TextInputField,
    },
  ];


  return (
    <>
      <LoaderBackdrop
        open={loading}
        handleClose={() => setLoading(false)}
      />

      <LicensePlateModal
        isLicensePlateModalOpen={isLicensePlateModalOpen}
        pullData={pullData}
        values={values}
        setIsLicensePlateModalOpen={setIsLicensePlateModalOpen}
        onClose={() => setIsLicensePlateModalOpen(false)}
      />

      {!!isTrimsModalOpen && (
        <TrimsModal
          trimModalPosition={isTrimsModalOpen}
          kbbTrims={kbbTrims}
          nadaTrims={nadaTrims}
          handleClose={handleCloseTrim}
          handleSave={handleSaveTrim}
        />
      )}
      <FormPart
        name="Asset"
        config={formConfig}
        marginTop={1}
        marginBottom={2}
      />
    </>
  );
};
